import React, { useState, useRef, useEffect } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import HardBreak from '@tiptap/extension-hard-break';
import { 
  Bold, 
  Italic, 
  Strikethrough, 
  Code, 
  List, 
  ListOrdered, 
  Quote, 
  Minus,
  Heading1,
  Heading2,
  Heading3,
  Undo,
  Redo,
  Tag,
  ChevronDown,
  X
} from 'lucide-react';

/**
 * TipTapEditor Component
 * Beautiful rich text editor with stunning toolbar and Gmail-style line breaks
 */
export default function TipTapEditor({ 
  editor, 
  content, 
  onUpdate, 
  editable = true,
  currentDocument,
  focuses = [],
  onFocusChange
}) {
  const [showFocusDropdown, setShowFocusDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowFocusDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle focus toggle
  const handleToggleFocus = (focusId) => {
    if (!currentDocument || !onFocusChange) return;
    
    const currentFocusIds = currentDocument.focus_ids || [];
    const newFocusIds = currentFocusIds.includes(focusId)
      ? currentFocusIds.filter(id => id !== focusId)
      : [...currentFocusIds, focusId];
    
    onFocusChange(newFocusIds);
  };

  // Get currently selected focus names for display
  const getSelectedFocusNames = () => {
    if (!currentDocument?.focus_ids || currentDocument.focus_ids.length === 0) {
      return 'Focus';
    }
    const names = currentDocument.focus_ids
      .map(id => focuses.find(f => f.id === id)?.title)
      .filter(Boolean);
    if (names.length === 0) return 'Focus';
    if (names.length === 1) return names[0];
    return names[0]; // Show first focus name even if multiple
  };

  // Get focus count for badge
  const getFocusCount = () => {
    if (!currentDocument?.focus_ids || currentDocument.focus_ids.length === 0) {
      return 0;
    }
    return currentDocument.focus_ids.length;
  };

  // Update editor content when prop changes (e.g., loading a different document)
  React.useEffect(() => {
    if (editor && content && !editor.isFocused) {
      const jsonContent = typeof content === 'string' ? JSON.parse(content) : content;
      const currentContent = editor.getJSON();
      // Only update if content is actually different
      if (JSON.stringify(currentContent) !== JSON.stringify(jsonContent)) {
        editor.commands.setContent(jsonContent);
      }
    }
  }, [editor, content]);

  if (!editor) {
    return <div className="flex items-center justify-center h-full">
      <p className="text-neutral-400 text-sm">Loading editor...</p>
    </div>;
  }

  const ToolbarButton = ({ onClick, active, disabled, children, title }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-2 rounded-lg transition-all duration-200 ${
        active
          ? 'bg-purple-100 text-purple-700 shadow-sm'
          : 'text-neutral-600 hover:bg-neutral-100 hover:text-purple-600'
      } ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:scale-110'}`}
    >
      {children}
    </button>
  );

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Gorgeous Floating Toolbar */}
      {editable && (
        <div className="flex-shrink-0 px-6 py-4 border-b border-neutral-200 bg-gradient-to-b from-white to-neutral-50/50">
          <div className="flex items-center gap-1 flex-wrap">
            {/* Undo/Redo */}
            <div className="flex items-center gap-1 pr-2 mr-2 border-r border-neutral-200">
              <ToolbarButton
                onClick={() => editor.chain().focus().undo().run()}
                disabled={!editor.can().undo()}
                title="Undo"
              >
                <Undo size={18} />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor.chain().focus().redo().run()}
                disabled={!editor.can().redo()}
                title="Redo"
              >
                <Redo size={18} />
              </ToolbarButton>
            </div>

            {/* Headings + Focus Selector */}
            <div className="flex items-center gap-1 pr-2 mr-2 border-r border-neutral-200">
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                active={editor.isActive('heading', { level: 1 })}
                title="Heading 1"
              >
                <Heading1 size={18} />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                active={editor.isActive('heading', { level: 2 })}
                title="Heading 2"
              >
                <Heading2 size={18} />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                active={editor.isActive('heading', { level: 3 })}
                title="Heading 3"
              >
                <Heading3 size={18} />
              </ToolbarButton>
              
              {/* Focus Selector - Compact */}
              <div className="relative pl-2 ml-2 border-l border-neutral-200" ref={dropdownRef}>
                <button
                  onClick={() => setShowFocusDropdown(!showFocusDropdown)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                    getFocusCount() > 0
                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                      : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                  }`}
                  title={getFocusCount() > 0 ? `Tagged with ${getFocusCount()} focus${getFocusCount() > 1 ? 'es' : ''}` : 'Add to focus'}
                >
                  <Tag size={14} />
                  <span className="max-w-[120px] truncate">{getSelectedFocusNames()}</span>
                  {getFocusCount() > 1 && (
                    <span className="ml-0.5 px-1.5 py-0.5 bg-white/20 rounded-full text-[10px] font-semibold">
                      {getFocusCount()}
                    </span>
                  )}
                </button>

                {/* Focus Dropdown */}
                {showFocusDropdown && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-neutral-200 rounded-xl shadow-xl z-50 py-2 max-h-80 overflow-y-auto">
                    <div className="px-3 py-2 text-xs font-semibold text-neutral-400 uppercase border-b border-neutral-100">
                      Select Focus
                    </div>
                    {focuses.length === 0 ? (
                      <div className="px-3 py-4 text-center text-sm text-neutral-400">
                        No focuses created yet
                      </div>
                    ) : (
                      focuses
                        .sort((a, b) => a.title.localeCompare(b.title))
                        .map(focus => {
                          const isSelected = currentDocument?.focus_ids?.includes(focus.id);
                          return (
                            <button
                              key={focus.id}
                              onClick={() => handleToggleFocus(focus.id)}
                              className={`w-full px-3 py-2.5 text-left text-sm hover:bg-neutral-50 transition-colors flex items-center justify-between group ${
                                isSelected ? 'bg-purple-50 text-purple-700' : 'text-neutral-700'
                              }`}
                            >
                              <span className="flex items-center gap-2">
                                <Tag size={14} className={isSelected ? 'text-purple-500' : 'text-neutral-400'} />
                                {focus.title}
                              </span>
                              {isSelected && (
                                <X size={14} className="text-purple-500" />
                              )}
                            </button>
                          );
                        })
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Text Formatting */}
            <div className="flex items-center gap-1 pr-2 mr-2 border-r border-neutral-200">
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleBold().run()}
                active={editor.isActive('bold')}
                title="Bold"
              >
                <Bold size={18} />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleItalic().run()}
                active={editor.isActive('italic')}
                title="Italic"
              >
                <Italic size={18} />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleStrike().run()}
                active={editor.isActive('strike')}
                title="Strikethrough"
              >
                <Strikethrough size={18} />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleCode().run()}
                active={editor.isActive('code')}
                title="Inline Code"
              >
                <Code size={18} />
              </ToolbarButton>
            </div>

            {/* Lists & Blocks */}
            <div className="flex items-center gap-1">
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                active={editor.isActive('bulletList')}
                title="Bullet List"
              >
                <List size={18} />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                active={editor.isActive('orderedList')}
                title="Numbered List"
              >
                <ListOrdered size={18} />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                active={editor.isActive('blockquote')}
                title="Quote"
              >
                <Quote size={18} />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor.chain().focus().setHorizontalRule().run()}
                title="Horizontal Line"
              >
                <Minus size={18} />
              </ToolbarButton>
            </div>
          </div>
        </div>
      )}

      {/* Beautiful Editor Content */}
      <div className="flex-1 overflow-y-auto bg-white">
        <style>{`
          .ProseMirror {
            min-height: 500px;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            color: #1f2937;
            line-height: 1.8;
          }
          .ProseMirror:focus {
            outline: none;
          }
          .ProseMirror p {
            margin: 0;
          }
          .ProseMirror p.is-editor-empty:first-child::before {
            content: 'Start writing something amazing...';
            float: left;
            color: #cbd5e0;
            pointer-events: none;
            height: 0;
            font-style: italic;
          }
          .ProseMirror h1 {
            font-size: 2.5em;
            font-weight: 700;
            margin-top: 1em;
            margin-bottom: 0.5em;
            line-height: 1.2;
            color: #111827;
            letter-spacing: -0.02em;
          }
          .ProseMirror h2 {
            font-size: 2em;
            font-weight: 700;
            margin-top: 1.5em;
            margin-bottom: 0.5em;
            line-height: 1.3;
            color: #1f2937;
            letter-spacing: -0.01em;
          }
          .ProseMirror h3 {
            font-size: 1.5em;
            font-weight: 600;
            margin-top: 1.5em;
            margin-bottom: 0.5em;
            line-height: 1.4;
            color: #374151;
          }
          .ProseMirror ul, .ProseMirror ol {
            padding-left: 1.8em;
            margin: 1.5em 0;
          }
          .ProseMirror li {
            margin: 0.5em 0;
            line-height: 1.8;
          }
          .ProseMirror li p {
            margin: 0.25em 0;
          }
          .ProseMirror blockquote {
            border-left: 4px solid #7A47F5;
            padding-left: 1.5em;
            margin-left: 0;
            margin: 2em 0;
            color: #4b5563;
            font-style: italic;
            background: linear-gradient(to right, rgba(122, 71, 245, 0.03), transparent);
            padding: 1em 1.5em;
            border-radius: 0 8px 8px 0;
          }
          .ProseMirror code {
            background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
            padding: 0.3em 0.5em;
            border-radius: 6px;
            font-family: 'Fira Code', 'Monaco', 'Courier New', monospace;
            font-size: 0.9em;
            color: #7A47F5;
            font-weight: 500;
            box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          }
          .ProseMirror pre {
            background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
            color: #f9fafb;
            padding: 1.5em;
            border-radius: 12px;
            overflow-x: auto;
            margin: 2em 0;
            box-shadow: 0 10px 25px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.1) inset;
          }
          .ProseMirror pre code {
            background: transparent;
            color: inherit;
            padding: 0;
            box-shadow: none;
            font-size: 0.95em;
            line-height: 1.6;
          }
          .ProseMirror strong {
            font-weight: 700;
            color: #111827;
          }
          .ProseMirror em {
            font-style: italic;
            color: #374151;
          }
          .ProseMirror hr {
            border: none;
            height: 2px;
            background: linear-gradient(to right, transparent, #e5e7eb 50%, transparent);
            margin: 3em 0;
          }
          .ProseMirror a {
            color: #7A47F5;
            text-decoration: underline;
            text-decoration-color: rgba(122, 71, 245, 0.3);
            text-underline-offset: 3px;
            transition: all 0.2s;
          }
          .ProseMirror a:hover {
            text-decoration-color: #7A47F5;
            color: #6A3AD5;
          }
        `}</style>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

